/**
 * GiornataPage — calendario per competizione + pannello "Calcola giornata".
 *
 * Andrea 2026-05-23: tab-bar a bottoni (analoga a /lega) con 4 voci:
 *
 *   - Campionato       → CalendarioPartite della competizione campionato
 *   - Champions League → CalendarioPartite della competizione CL
 *   - Supercoppa       → CalendarioPartite della competizione Supercoppa
 *   - Calcola giornata → selettore giornata + risultati + azioni admin
 *
 * Andrea 2026-09-10: la pagina accetta `?competizione=<id>` (ci arriva
 * il blocco Andamento della home) e si apre sul calendario di quella
 * competizione. Il tab scelto a mano vince sempre sull'URL.
 *
 * I primi 3 bottoni appaiono solo se la rispettiva competizione esiste
 * nella stagione attiva. "Calcola giornata" e' sempre disponibile.
 * Default selezionato: Campionato (o la prima competizione disponibile).
 */

import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { useSquadre } from "../../hooks/useFase7";
import { useCompetizioniStagione } from "../../hooks/useFase5";
import { useStagioneAttiva } from "../../hooks/useStagione";
import { GiornataSelector } from "../common/GiornataSelector";
import { MobileShell } from "../shell/MobileShell";
import { CalendarioPartite } from "../lega/CalendarioPartite";
import {
  StatoGiornata,
  useAnnullaGiornata,
  useAnnullamenti,
  useCalcolaGiornata,
  useGiornata,
  useRiapriGiornata,
  useRicalcolaGiornata,
  useRisultatiGiornata,
} from "../../hooks/useFase4";
import { etichetta } from "../common/etichette";
import { PartiteGiornata } from "./PartiteGiornata";
import { localeCorrente } from "../../i18n";

type Tab = "campionato" | "cl" | "supercoppa" | "calcola";

const STATO_COLOR: Record<StatoGiornata, string> = {
  da_giocare: "bg-fg-muted/20 text-fg-muted",
  voti_caricati: "bg-info/20 text-info",
  calcolata: "bg-success/20 text-success",
  annullata: "bg-danger/30 text-danger",
};

export function GiornataPage() {
  const { user } = useAuth();
  const isAdmin =
    user?.ruolo_lega === "presidente_lega" ||
    user?.ruolo_lega === "admin" ||
    user?.ruoli_aggiuntivi?.includes("admin") ||
    user?.ruoli_aggiuntivi?.includes("presidente_lega");

  const { data: stagioneAttiva } = useStagioneAttiva();
  const { data: squadre } = useSquadre();
  const stagioneId = stagioneAttiva?.id ?? null;
  const squadraNome = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of squadre ?? []) m.set(s.id, s.nome);
    return (id: string): string => m.get(id) ?? `${id.slice(0, 8)}…`;
  }, [squadre]);
  const [giornataId, setGiornataId] = useState("");
  const [showAnnulla, setShowAnnulla] = useState(false);
  const [motivazione, setMotivazione] = useState("");
  const [actionError, setActionError] = useState<string | null>(null);

  // Competizioni della stagione attiva: una per tipo.
  const { data: competizioniStagione } = useCompetizioniStagione(stagioneId);
  const campionato = competizioniStagione?.find((c) => c.tipo === "campionato") ?? null;
  const championsLeague = competizioniStagione?.find((c) => c.tipo === "champions_league") ?? null;
  const supercoppa = competizioniStagione?.find((c) => c.tipo === "supercoppa") ?? null;

  // Tab corrente: default = prima competizione disponibile in quest'ordine.
  // Se nessuna competizione esiste, fallback su "calcola" (sempre presente).
  const defaultTab: Tab = campionato
    ? "campionato"
    : championsLeague
      ? "cl"
      : supercoppa
        ? "supercoppa"
        : "calcola";
  // ?competizione=<id> → tab corrispondente (link dalla home).
  const [searchParams] = useSearchParams();
  const compRichiesta = searchParams.get("competizione");
  const tabDaUrl: Tab | null =
    searchParams.get("tab") === "calcola"
      ? "calcola"
      : !compRichiesta
        ? null
        : compRichiesta === campionato?.id
          ? "campionato"
          : compRichiesta === championsLeague?.id
            ? "cl"
            : compRichiesta === supercoppa?.id
              ? "supercoppa"
              : null;
  // Il tab non sta in useState "secco": le competizioni arrivano dopo il
  // primo render, e con l'inizializzazione una volta sola la pagina si
  // apriva su "Calcola giornata" (Andrea 2026-09-10). Qui comanda la
  // scelta dell'utente, poi l'URL, poi il default.
  const [tabScelto, setTabScelto] = useState<Tab | null>(null);
  const tab: Tab = tabScelto ?? tabDaUrl ?? defaultTab;
  const setTab = setTabScelto;

  // Competizione attiva nel calendario in base al tab selezionato.
  const calCompId: string | null =
    tab === "campionato"
      ? (campionato?.id ?? null)
      : tab === "cl"
        ? (championsLeague?.id ?? null)
        : tab === "supercoppa"
          ? (supercoppa?.id ?? null)
          : null;

  const { data: g } = useGiornata(giornataId || null);
  const { data: ris } = useRisultatiGiornata(giornataId || null);
  const { data: annullamenti } = useAnnullamenti(giornataId || null);
  const calcolaMut = useCalcolaGiornata();
  const annullaMut = useAnnullaGiornata();
  const ricalcolaMut = useRicalcolaGiornata();
  const riapriMut = useRiapriGiornata();

  async function fai(action: "calcola" | "ricalcola") {
    if (!giornataId) return;
    setActionError(null);
    try {
      if (action === "calcola") await calcolaMut.mutateAsync(giornataId);
      else await ricalcolaMut.mutateAsync(giornataId);
    } catch (e) {
      const err = e as { response?: { data?: { detail?: string } }; message?: string };
      setActionError(err.response?.data?.detail ?? err.message ?? "errore");
    }
  }

  async function submitAnnulla() {
    if (!giornataId || !motivazione.trim()) return;
    setActionError(null);
    try {
      await annullaMut.mutateAsync({ giornataId, motivazione: motivazione.trim() });
      setShowAnnulla(false);
      setMotivazione("");
    } catch (e) {
      const err = e as { response?: { data?: { detail?: string } }; message?: string };
      setActionError(err.response?.data?.detail ?? err.message ?? "errore");
    }
  }

  return (
    <MobileShell logoSuffix="CALENDARIO">
      {g?.banner_ricalcolo_in_corso && (
        <div className="bg-danger/20 border border-danger/40 text-danger rounded-card p-3 text-center text-sm font-bold">
          ⚠️ Giornata annullata — Ricalcolo in corso. I risultati visibili al momento NON sono
          ufficiali.
        </div>
      )}
      <h2 className="font-display text-3xl text-accent tracking-wide">Calendario</h2>

      {/* Tab-bar a bottoni: una voce per competizione + "Calcola giornata".
            I bottoni delle competizioni appaiono solo se la rispettiva
            competizione esiste nella stagione attiva. */}
      <div className="flex gap-2 justify-center flex-wrap">
        {campionato && (
          <button
            type="button"
            onClick={() => setTab("campionato")}
            className={`pill ${
              tab === "campionato"
                ? "bg-accent text-ink font-bold"
                : "bg-primary-deep/40 text-fg-muted hover:bg-primary-deep/60"
            }`}
          >
            📊 Campionato
          </button>
        )}
        {championsLeague && (
          <button
            type="button"
            onClick={() => setTab("cl")}
            className={`pill ${
              tab === "cl"
                ? "bg-accent text-ink font-bold"
                : "bg-primary-deep/40 text-fg-muted hover:bg-primary-deep/60"
            }`}
          >
            🏆 Coppa
          </button>
        )}
        {supercoppa && (
          <button
            type="button"
            onClick={() => setTab("supercoppa")}
            className={`pill ${
              tab === "supercoppa"
                ? "bg-accent text-ink font-bold"
                : "bg-primary-deep/40 text-fg-muted hover:bg-primary-deep/60"
            }`}
          >
            🥇 Supercoppa
          </button>
        )}
        <button
          type="button"
          onClick={() => setTab("calcola")}
          className={`pill ${
            tab === "calcola"
              ? "bg-accent text-ink font-bold"
              : "bg-primary-deep/40 text-fg-muted hover:bg-primary-deep/60"
          }`}
        >
          🧮 Calcola giornata
        </button>
      </div>

      {tab !== "calcola" && calCompId && (
        <CalendarioPartite stagioneId={stagioneId} competizioneId={calCompId} />
      )}

      {tab === "calcola" && (
        <>
          <div className="card">
            <GiornataSelector
              stagioneId={stagioneId}
              value={giornataId}
              onChange={setGiornataId}
              autoSelectFirstAperta
            />
          </div>

          {g && (
            <>
              <div className="card flex items-center justify-between flex-wrap gap-3">
                <div>
                  <h3 className="font-bold text-accent">
                    Giornata #{g.numero} (SA {g.numero_serie_a})
                  </h3>
                  <p className="text-xs text-fg-muted mt-1">
                    Versione corrente: <strong>{g.versione_calcolo_corrente}</strong> ·{" "}
                    {g.data_calcolo
                      ? `calcolata il ${new Date(g.data_calcolo).toLocaleString(localeCorrente())}`
                      : "non ancora calcolata"}
                  </p>
                </div>
                <span className={`pill ${STATO_COLOR[g.stato]}`}>{etichetta(g.stato)}</span>
              </div>

              {isAdmin && (
                <div className="card flex gap-2 flex-wrap">
                  {g.stato === "voti_caricati" && (
                    <>
                      <button
                        onClick={() => fai("calcola")}
                        disabled={calcolaMut.isPending}
                        className="btn-primary text-sm"
                      >
                        {calcolaMut.isPending ? "…" : "Calcola giornata"}
                      </button>
                      <button
                        onClick={async () => {
                          if (
                            !confirm(
                              "Riaprire la giornata? Lo stato torna a 'da_giocare'. I voti caricati restano nel DB ma la giornata sara' di nuovo modificabile.",
                            )
                          )
                            return;
                          setActionError(null);
                          try {
                            await riapriMut.mutateAsync(giornataId);
                          } catch (e) {
                            const err = e as {
                              response?: { data?: { detail?: string } };
                              message?: string;
                            };
                            setActionError(err.response?.data?.detail ?? err.message ?? "errore");
                          }
                        }}
                        disabled={riapriMut.isPending}
                        className="btn-ghost text-sm border border-warning/40 text-warning hover:bg-warning/10"
                        title="Rollback: torna a 'da_giocare' (es. import live ha chiuso indebitamente)"
                      >
                        {riapriMut.isPending ? "…" : "↺ Riapri giornata"}
                      </button>
                    </>
                  )}
                  {g.stato === "calcolata" && (
                    <>
                      <button
                        onClick={() => setShowAnnulla(true)}
                        className="btn-ghost text-sm text-danger"
                      >
                        Annulla
                      </button>
                      <button
                        onClick={() => fai("ricalcola")}
                        disabled={ricalcolaMut.isPending}
                        className="btn-ghost text-sm"
                      >
                        {ricalcolaMut.isPending ? "…" : "Ricalcola"}
                      </button>
                      <p className="w-full text-xs text-fg-muted">
                        Per correggere a mano il risultato di una partita usa ✎ nelle partite qui
                        sotto: la modifica resta anche se ricalcoli.
                      </p>
                    </>
                  )}
                  {g.stato === "annullata" && (
                    <button
                      onClick={() => fai("ricalcola")}
                      disabled={ricalcolaMut.isPending}
                      className="btn-primary text-sm"
                    >
                      {ricalcolaMut.isPending ? "…" : "Ricalcola (chiudi annullamento)"}
                    </button>
                  )}
                </div>
              )}

              {actionError && (
                <div className="card bg-danger/10 border-danger/30 text-sm text-danger">
                  {actionError}
                </div>
              )}

              <div className="card p-0 overflow-hidden">
                <div className="bg-primary-deep/40 px-4 py-2 text-xs font-bold uppercase tracking-wider flex gap-2">
                  <span className="w-8">#</span>
                  <span className="flex-1">Squadra</span>
                  <span className="w-20 text-right">Voto</span>
                  <span className="w-16 text-right">+Cap</span>
                  <span className="w-16 text-right">+Dif</span>
                  <span className="w-20 text-right">Totale</span>
                  <span className="w-12 text-right">Gol</span>
                </div>
                {ris && ris.length === 0 && (
                  <div className="p-6 text-center text-fg-muted">
                    Nessun risultato per questa giornata.
                  </div>
                )}
                {ris?.map((r, i) => (
                  <div
                    key={r.id}
                    className="flex gap-2 px-4 py-2 border-b border-primary/10 last:border-b-0 text-sm"
                  >
                    <span className="w-8 text-fg-muted">{i + 1}</span>
                    <span className="flex-1 font-bold truncate">{squadraNome(r.squadra_id)}</span>
                    <span className="w-20 text-right font-mono">
                      {Number(r.punteggio_voto).toFixed(2)}
                    </span>
                    <span className="w-16 text-right font-mono">
                      {Number(r.bonus_capitano).toFixed(2)}
                    </span>
                    <span className="w-16 text-right font-mono">
                      {Number(r.bonus_difesa).toFixed(2)}
                    </span>
                    <span className="w-20 text-right font-mono font-bold text-accent">
                      {Number(r.punteggio_totale).toFixed(2)}
                    </span>
                    <span className="w-12 text-right font-mono">{r.gol_fantacalcio}</span>
                  </div>
                ))}
              </div>

              <PartiteGiornata
                giornataId={giornataId}
                stagioneId={stagioneId}
                calcolata={g.stato === "calcolata"}
                puoModificare={!!isAdmin}
                nomeSquadra={squadraNome}
              />

              {annullamenti && annullamenti.length > 0 && (
                <div className="card">
                  <h3 className="font-bold text-accent mb-2">Storico annullamenti</h3>
                  <ul className="space-y-2 text-sm">
                    {annullamenti.map((a) => (
                      <li key={a.id} className="border-l-2 border-danger/40 pl-3">
                        <div className="text-xs text-fg-muted">
                          {new Date(a.data_annullamento).toLocaleString(localeCorrente())} — v
                          {a.versione_annullata}
                          {a.nuova_versione_calcolata && ` → v${a.nuova_versione_calcolata}`}
                        </div>
                        <div>{a.motivazione}</div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </>
      )}

      {showAnnulla && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-bg border border-accent/30 rounded-xl max-w-md w-full shadow-2xl">
            <div className="bg-primary-deep px-6 py-4 border-b border-accent/30">
              <h3 className="font-display text-xl text-accent">Annulla giornata</h3>
            </div>
            <div className="p-6 space-y-3">
              <p className="text-sm text-fg-muted">
                L'annullamento marca obsoleti tutti i risultati attivi e segnala la giornata come
                "Ricalcolo in corso". È un'operazione audit-completa, non distruttiva.
              </p>
              <textarea
                value={motivazione}
                onChange={(e) => setMotivazione(e.target.value)}
                rows={3}
                placeholder="Motivazione obbligatoria…"
                className="w-full px-3 py-2 bg-primary-deep border border-primary-soft rounded-lg text-fg text-sm focus:outline-none focus:ring-2 focus:ring-accent"
              />
            </div>
            <div className="bg-primary-deep/40 px-6 py-4 border-t border-primary-soft flex justify-between">
              <button onClick={() => setShowAnnulla(false)} className="btn-ghost text-sm">
                Annulla
              </button>
              <button
                onClick={submitAnnulla}
                disabled={!motivazione.trim() || annullaMut.isPending}
                className="btn-primary text-sm text-danger disabled:opacity-50"
              >
                {annullaMut.isPending ? "…" : "Conferma annullamento"}
              </button>
            </div>
          </div>
        </div>
      )}
    </MobileShell>
  );
}
